import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { OAuthStorageService } from '@qaroni-app/auth/services/o-auth-storage.service';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { BehaviorSubject } from 'rxjs';
import { WorkplaceApiObject } from '../types/workplace-response';
import { WorkplaceStatusEnum } from '../types/workplace-status.enum';
import { WorkplacePolicyUtils } from '../utils/workplace-policy-utils';
import { WorkplaceHttpService } from './workplace-http.service';
import { WorkplaceSnackbarsService } from './workplace-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class WorkplacePolicyService extends QaroniIntermediate {
  private readonly strictPolicySubject = new BehaviorSubject<boolean>(null);

  public readonly strictPolicy$ = this.strictPolicySubject.asObservable();

  private http = inject(WorkplaceHttpService);
  private oAuthStorage = inject(OAuthStorageService);
  private snackbars = inject(WorkplaceSnackbarsService);

  private policyCompanyID: number = 0;
  private isStrictRequestPending = false;

  public loadStrictPolicy(companyID: number): void {
    const isSameCompany = companyID === this.policyCompanyID;

    if (
      isSameCompany &&
      (this.strictPolicySubject.value !== null || this.isStrictRequestPending)
    ) {
      return;
    }

    this.refreshStrictPolicy(companyID);
  }

  public refreshStrictPolicy(companyID: number, notifyChange = false): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    if (companyID !== this.policyCompanyID) {
      this.policyCompanyID = companyID;
      this.strictPolicySubject.next(null);
    }

    const params: Params = {
      status: WorkplaceStatusEnum.ACTIVE,
      page: 1,
      pagination: 1000,
    };

    this.isStrictRequestPending = true;
    this.http.list$(companyID, params).subscribe({
      next: response =>
        this.nextRefreshStrictPolicy(response, companyID, notifyChange),
      error: () => this.errorRefreshStrictPolicy(companyID),
    });
  }

  private nextRefreshStrictPolicy = (
    response: WorkplaceApiObject,
    companyID: number,
    notifyChange: boolean
  ): void => {
    if (companyID !== this.policyCompanyID) {
      return;
    }

    this.isStrictRequestPending = false;
    const previousStrictPolicy = this.strictPolicySubject.value;
    const hasStrictPolicy = WorkplacePolicyUtils.hasStrict(response.result);

    this.strictPolicySubject.next(hasStrictPolicy);

    if (
      notifyChange &&
      previousStrictPolicy !== null &&
      previousStrictPolicy !== hasStrictPolicy
    ) {
      this.notifyStrictPolicyChange(hasStrictPolicy);
    }
  };

  private notifyStrictPolicyChange(hasStrictPolicy: boolean): void {
    if (hasStrictPolicy) {
      this.snackbars.successStrictPolicyEnabled();
      return;
    }

    this.snackbars.warningStrictPolicyDisabled();
  }

  private errorRefreshStrictPolicy = (companyID: number): void => {
    if (companyID !== this.policyCompanyID) {
      return;
    }

    this.isStrictRequestPending = false;
    this.strictPolicySubject.next(false);
  };
}
